import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    useListOrganizations,
    useCreateEvent,
    useUpdateEvent,
    useGetEventDetails,
    useListEventCategories,
    useUploadEventImage
} from '../services/appService';
import { useAuth } from '../context/AuthContext';

function EventFormModal({ open, onClose, onSave, event: initialEvent, appId }) {
    const { isOrgAdmin, getOrgIds } = useAuth();
    const [event, setEvent] = useState({}); // Initialize with empty object
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [organizations, setOrganizations] = useState([]);
    const [loadingOrgs, setLoadingOrgs] = useState(false);
    const [availableCategories, setAvailableCategories] = useState([]); // State for categories
    const [loadingCategories, setLoadingCategories] = useState(false); // Loading state for categories
    const [isEditMode, setIsEditMode] = useState(false);
    const [isUploading, setIsUploading] = useState(false); // State for image upload loading
    const fileInputRef = useRef(null); // Ref for hidden file input

    const listOrgsHook = useListOrganizations();
    const createEventHook = useCreateEvent();
    const updateEventHook = useUpdateEvent();
    const getEventDetailsHook = useGetEventDetails(); // Hook to fetch full details
    const listCategoriesHook = useListEventCategories(); // Hook instance
    const uploadEventImageHook = useUploadEventImage(); // Use the specific hook

    // Fetch organizations for dropdown
    const fetchOrganizations = useCallback(async () => {
        if (!appId) return;
        setLoadingOrgs(true);
        try {
            const data = await listOrgsHook(appId);
            setOrganizations(data || []);
        } catch (err) {
            console.error("Error fetching organizations:", err);
            setError('Failed to load organizations list.'); // Inform user
        } finally {
            setLoadingOrgs(false);
        }
    }, [appId, listOrgsHook]);

    // Fetch event categories for dropdown
    const fetchCategories = useCallback(async () => {
        if (!appId) return;
        setLoadingCategories(true);
        try {
            const data = await listCategoriesHook(appId);
            // Filter out admin-only categories for the dropdown
            setAvailableCategories((data || []).filter(cat => !cat.is_admin_only));
        } catch (err) {
            console.error("Error fetching event categories:", err);
            setError(prev => prev ? `${prev}\nFailed to load categories list.` : 'Failed to load categories list.');
        } finally {
            setLoadingCategories(false);
        }
    }, [appId, listCategoriesHook]);

    useEffect(() => {
        if(open) { // Fetch only when modal is open
            fetchOrganizations();
            fetchCategories();
        }
    }, [open, fetchOrganizations, fetchCategories]);

    // Effect to initialize form state based on initialEvent and open
    useEffect(() => {
        if (!open) return;
        if (initialEvent && initialEvent.id) {
            // Edit mode: fetch full details
            setIsEditMode(true);
            setLoading(true);
            setError('');
            console.log("[EventFormModal] Edit mode detected. Fetching details for event:", initialEvent.id);
            getEventDetailsHook(appId, initialEvent.id)
                .then(fullEventData => {
                    console.log("[EventFormModal] Received full event data:", fullEventData);
                    // Format date to YYYY-MM-DD
                    if (fullEventData.date) {
                        const d = new Date(fullEventData.date);
                        fullEventData.date = isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
                    }
                    // Remove 'Um ' prefix from times
                    if (fullEventData.time?.startsWith('Um ')) fullEventData.time = fullEventData.time.substring(3);
                    if (fullEventData.end_time?.startsWith('Um ')) fullEventData.end_time = fullEventData.end_time.substring(3);
                    setEvent(fullEventData || {});
                })
                .catch(err => {
                    console.error("Error fetching full event details:", err);
                    setError(err.message || 'Failed to load event details for editing.');
                    setEvent({});
                })
                .finally(() => setLoading(false));
        } else if (initialEvent) {
            // Prefill form with provided event data (from AI)
            setIsEditMode(false);
            setEvent(initialEvent);
            setLoading(false);
            setError('');
        } else {
            // Fresh create mode: initialize to defaults
            setIsEditMode(false);
            const orgAdminOrgId = isOrgAdmin() && getOrgIds().length > 0 ? getOrgIds()[0] : null;
            setEvent({
                title: '', description: '', date: '', time: '', end_time: '',
                location: '', category: '', image_url: '', organizer_id: null,
                organization_id: orgAdminOrgId, is_published: true, recurrence_rule: '',
            });
            setLoading(false);
            setError('');
        }
    }, [initialEvent, open, appId, getEventDetailsHook]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEvent(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleOrgChange = (event) => {
        setEvent(prev => ({
            ...prev,
            organization_id: event.target.value === '' ? null : event.target.value,
        }));
    };

    // Handler for triggering file input click
    const handleUploadButtonClick = () => {
        fileInputRef.current?.click();
    };

    // Handler for file selection
    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !appId) {
            return;
        }

        setIsUploading(true);
        setError('');
        try {
            // No need to specify pathPrefix here, hook handles it
            const result = await uploadEventImageHook(appId, file);
            setEvent(prev => ({ ...prev, image_url: result.url })); // Set the image URL
            console.log('Event image uploaded:', result.url);
        } catch (err) {
            console.error("Error uploading event image:", err);
            setError(err.message || 'Failed to upload image.');
        } finally {
            setIsUploading(false);
            // Reset file input value
            if(fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };
    
    // Handler to remove image URL
    const handleRemoveImage = () => {
        setEvent(prev => ({ ...prev, image_url: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Basic Frontend Validation (can be enhanced)
        if (!event.title || !event.date || !event.time || !event.category) {
            setError('Title, Date, Time, and Category are required.');
            setLoading(false);
            return;
        }

        // Prepare data for submission
        const submissionData = { ...event };
        // Convert empty string org ID back to null if necessary
        if (submissionData.organization_id === '') {
            submissionData.organization_id = null;
        }
        // Ensure is_published is a boolean
        submissionData.is_published = Boolean(submissionData.is_published);
        // Ensure organizer_id is handled (might need logic based on auth context)
        // submissionData.organizer_id = ... // Set based on logged-in user?

        // Ensure recurrence_rule is null if empty string
        if (submissionData.recurrence_rule === '') {
            submissionData.recurrence_rule = null;
        }

        // Format time string before sending
        if (submissionData.time) {
            submissionData.time = `Um ${submissionData.time}`;
        }
        // Format end_time string if it exists
        if (submissionData.end_time) {
            submissionData.end_time = `Um ${submissionData.end_time}`;
        } else {
            submissionData.end_time = null; // Ensure null if empty
        }

        try {
            if (isEditMode) {
                console.log("[EventFormModal] Updating event:", submissionData);
                await updateEventHook(appId, event.id, submissionData);
            } else {
                console.log("[EventFormModal] Creating event:", submissionData);
                await createEventHook(appId, submissionData);
            }
            onSave(); // Call parent onSave to refresh list and close modal
        } catch (err) {
            console.error("Error saving event:", err);
            setError(err.message || 'Failed to save event.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{isEditMode ? 'Edit Event' : 'Create New Event'}</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="title"
                            label="Event Title"
                            name="title"
                            autoFocus
                            value={event.title || ''}
                            onChange={handleChange}
                        />
                        <TextField
                            margin="normal"
                            fullWidth
                            id="description"
                            label="Description"
                            name="description"
                            multiline
                            rows={4}
                            value={event.description || ''}
                            onChange={handleChange}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="date"
                            label="Date"
                            name="date"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={event.date || ''}
                            onChange={handleChange}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="time"
                            label="Start Time"
                            name="time"
                            type="time"
                            InputLabelProps={{ shrink: true }}
                            value={event.time || ''}
                            onChange={handleChange}
                        />
                        <TextField
                            margin="normal"
                            fullWidth
                            id="end_time"
                            label="End Time (Optional)"
                            name="end_time"
                            type="time"
                            InputLabelProps={{ shrink: true }}
                            value={event.end_time || ''}
                            onChange={handleChange}
                        />
                        <TextField
                            margin="normal"
                            fullWidth
                            id="location"
                            label="Location"
                            name="location"
                            value={event.location || ''}
                            onChange={handleChange}
                        />
                        <FormControl fullWidth margin="normal" required disabled={loadingCategories}>
                            <InputLabel id="event-category-select-label">Category</InputLabel>
                            <Select
                                labelId="event-category-select-label"
                                id="category"
                                name="category"
                                value={event.category || ''}
                                label="Category"
                                onChange={handleChange}
                            >
                                <MenuItem value="">
                                    <em>Select a category...</em>
                                </MenuItem>
                                {availableCategories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.name}>
                                        {cat.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {availableCategories.length === 0 && !loadingCategories &&
                                <Alert severity="warning" sx={{mt: 1}}>No categories available. Add them in Event Categories settings.</Alert>
                            }
                        </FormControl>
                        <TextField
                            margin="normal"
                            fullWidth
                            id="image_url"
                            label="Image URL (Optional - Auto-filled on Upload)"
                            name="image_url"
                            value={event.image_url || ''}
                            onChange={handleChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {event.image_url && (
                                            <IconButton
                                                aria-label="remove image url"
                                                onClick={handleRemoveImage}
                                                edge="end"
                                                size="small"
                                                sx={{mr: 0.5}}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        <IconButton
                                            aria-label="upload event image"
                                            onClick={handleUploadButtonClick}
                                            edge="end"
                                            disabled={isUploading}
                                        >
                                            {isUploading ? <CircularProgress size={24} /> : <UploadFileIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                            accept="image/*"
                        />
                        <TextField
                            margin="normal"
                            fullWidth
                            id="recurrence_rule"
                            label="Recurrence Rule (Optional - iCal RRULE format)"
                            name="recurrence_rule"
                            value={event.recurrence_rule || ''}
                            onChange={handleChange}
                            helperText="Example: FREQ=WEEKLY;BYDAY=MO;INTERVAL=1 (Repeats weekly on Monday)"
                        />
                        {isOrgAdmin() ? (
                            // Org admin: show read-only organization name
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Organization"
                                value={organizations.find(o => o.id === event.organization_id)?.name || 'Loading...'}
                                disabled
                            />
                        ) : (
                            <FormControl fullWidth margin="normal" disabled={loadingOrgs}>
                                <InputLabel id="organization-select-label">Organization (Optional)</InputLabel>
                                <Select
                                    labelId="organization-select-label"
                                    id="organization_id"
                                    name="organization_id"
                                    value={event.organization_id || ''}
                                    label="Organization (Optional)"
                                    onChange={handleOrgChange}
                                >
                                    <MenuItem value="">
                                        <em>None (Personal Event)</em>
                                    </MenuItem>
                                    {organizations.map((org) => (
                                        <MenuItem key={org.id} value={org.id}>
                                            {org.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={event.is_published || false}
                                    onChange={handleChange}
                                    name="is_published"
                                    color="primary"
                                />
                            }
                            label="Published"
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : (isEditMode ? 'Save Changes' : 'Create Event')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

EventFormModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    event: PropTypes.object, // Event object for editing, null/undefined for create
    appId: PropTypes.string.isRequired,
};

export default EventFormModal; 